import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuditFilters from './components/AuditFilters';
import AuditTable from './components/AuditTable';
import ExportModal from './components/ExportModal';
import PaginationControls from './components/PaginationControls';
import AuditSummaryCards from './components/AuditSummaryCards';

// Import localStorage service
import { getQueryHistory, calculateDashboardMetrics } from '../../services/localStorage';

const HistoryAndAuditTrail = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Transform localStorage query history to audit format
  const transformQueryToAudit = (query) => ({
    id: query.id,
    timestamp: new Date(query.timestamp),
    user: {
      name: query.user?.name || 'Usuario',
      email: query.user?.email || 'N/A',
      role: query.user?.role || 'Operador'
    },
    vin: query.vin,
    vehicle: query.vehicle ? {
      brand: query.vehicle.brand || 'N/A',
      model: query.vehicle.model || 'N/A',
      year: parseInt(query.vehicle.year) || 0,
      version: query.vehicle.version || 'N/A'
    } : null,
    status: query.status,
    latency: query.latency || 0,
    location: {
      city: query.location?.city || 'N/A',
      state: query.location?.state || 'N/A',
      country: query.location?.country || 'México',
      ip: query.location?.ip || 'N/A'
    },
    sessionId: `sess_${query.id?.slice(0, 8)}`,
    device: query.device || 'Desktop',
    browser: query.browser || 'Chrome',
    responseTime: query.latency ? query.latency + 200 : 0,
    dataSource: query.dataSource || 'CESVI México',
    apiVersion: query.apiVersion || 'v1.0',
    geofencing: true,
    dailyUsage: query.versionsCount || 0,
    authMethod: 'Password'
  });

  // Load data from localStorage
  const loadAuditData = () => {
    const queryHistory = getQueryHistory();
    return queryHistory.map(transformQueryToAudit);
  };

  // Calculate summary from localStorage data
  const calculateSummary = (data) => {
    const metrics = calculateDashboardMetrics();
    const totalQueries = data.length;
    const successfulQueries = data.filter(d => d.status === 'success').length;
    const avgLatency = totalQueries > 0
      ? Math.round(data.reduce((sum, d) => sum + (d.latency || 0), 0) / totalQueries)
      : 0;

    return {
      totalQueries,
      totalQueriesChange: totalQueries > 0 ? 100 : 0,
      successRate: totalQueries > 0 ? ((successfulQueries / totalQueries) * 100).toFixed(1) : 0,
      successRateChange: 0,
      activeUsers: 1,
      activeUsersChange: 0,
      avgLatency,
      avgLatencyChange: 0
    };
  };

  const [auditData, setAuditData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalQueries: 0,
    totalQueriesChange: 0,
    successRate: 0,
    successRateChange: 0,
    activeUsers: 1,
    activeUsersChange: 0,
    avgLatency: 0,
    avgLatencyChange: 0
  });

  // Load data on mount and refresh
  useEffect(() => {
    const data = loadAuditData();
    setAuditData(data);
    setFilteredData(data);
    setSummaryData(calculateSummary(data));
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...auditData];

    // Apply search filter
    if (filters?.vinSearch) {
      const searchTerm = filters?.vinSearch?.toLowerCase();
      filtered = filtered?.filter(item => 
        item?.vin?.toLowerCase()?.includes(searchTerm) ||
        item?.user?.name?.toLowerCase()?.includes(searchTerm) ||
        item?.user?.email?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply date range filter
    if (filters?.dateRange?.start) {
      const startDate = new Date(filters.dateRange.start);
      filtered = filtered?.filter(item => new Date(item.timestamp) >= startDate);
    }
    if (filters?.dateRange?.end) {
      const endDate = new Date(filters.dateRange.end);
      endDate?.setHours(23, 59, 59, 999);
      filtered = filtered?.filter(item => new Date(item.timestamp) <= endDate);
    }

    // Apply user filter
    if (filters?.user) {
      filtered = filtered?.filter(item => item?.user?.email === filters?.user);
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(item => item?.status === filters?.status);
    }

    // Apply brand filter
    if (filters?.brand) {
      filtered = filtered?.filter(item => 
        item?.vehicle?.brand?.toLowerCase() === filters?.brand?.toLowerCase()
      );
    }

    // Apply model filter
    if (filters?.model) {
      filtered = filtered?.filter(item => 
        item?.vehicle?.model?.toLowerCase()?.includes(filters?.model?.toLowerCase())
      );
    }

    // Apply IP filter
    if (filters?.ipAddress) {
      filtered = filtered?.filter(item => 
        item?.location?.ip?.includes(filters?.ipAddress)
      );
    }

    // Apply geo location filter
    if (filters?.geoLocation) {
      const locationMap = {
        'mexico-city': 'Ciudad de México',
        'guadalajara': 'Guadalajara',
        'monterrey': 'Monterrey',
        'puebla': 'Puebla',
        'tijuana': 'Tijuana'
      };
      const cityName = locationMap?.[filters?.geoLocation];
      if (cityName) {
        filtered = filtered?.filter(item => item?.location?.city === cityName);
      }
    }

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, auditData]);

  // Apply sorting
  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return filteredData;

    return [...filteredData]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      // Handle nested properties
      if (sortConfig?.key === 'user') {
        aValue = a?.user?.name;
        bValue = b?.user?.name;
      }

      // Handle different data types
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig?.direction === 'asc' 
          ? aValue?.getTime() - bValue?.getTime()
          : bValue?.getTime() - aValue?.getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig?.direction === 'asc' ? aValue?.localeCompare(bValue,'es-MX')
          : bValue?.localeCompare(aValue, 'es-MX');
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig?.direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);
  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      key: column,
      direction: prev?.key === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRowExpand = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(rowId)) {
      newExpanded?.delete(rowId);
    } else {
      newExpanded?.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = async (exportConfig) => {
    setIsLoading(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In a real application, this would generate and download the file
    console.log('Exportando datos con configuración:', exportConfig);
    
    // Create mock download
    const dataToExport = sortedData?.slice(0, exportConfig?.maxRecords);
    const filename = `auditoria_vinpro_${new Date()?.toISOString()?.split('T')?.[0]}.${exportConfig?.format}`;
    
    // Mock file creation based on format
    let content = '';
    if (exportConfig?.format === 'csv') {
      const headers = Object.keys(exportConfig?.includeColumns)?.filter(key => exportConfig?.includeColumns?.[key])?.join(',');
      content = headers + '\n' + dataToExport?.map(row => 
        Object.keys(exportConfig?.includeColumns)?.filter(key => exportConfig?.includeColumns?.[key])?.map(key => {
            if (key === 'timestamp') return new Date(row.timestamp)?.toLocaleString('es-MX');
            if (key === 'user') return row?.user?.name;
            if (key === 'vehicle') return `${row?.vehicle?.brand} ${row?.vehicle?.model}`;
            if (key === 'location') return `${row?.location?.city}, ${row?.location?.ip}`;
            return row?.[key] || '';
          })?.join(',')
      )?.join('\n');
    }
    
    // Create and trigger download
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link?.click();
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6 max-w-full">
          <Breadcrumbs />
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Historial y Auditoría
              </h1>
              <p className="text-muted-foreground">
                Seguimiento completo de consultas y cumplimiento normativo
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={16}
                onClick={() => {
                  const data = loadAuditData();
                  setAuditData(data);
                  setFilteredData(data);
                  setSummaryData(calculateSummary(data));
                }}
              >
                Actualizar
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
                onClick={() => setIsExportModalOpen(true)}
              >
                Exportar datos
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <AuditSummaryCards summaryData={summaryData} />

          {/* Filters */}
          <AuditFilters 
            onFiltersChange={handleFiltersChange}
            resultsCount={filteredData?.length}
          />

          {/* Main Content */}
          <div className="space-y-6">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Cargando datos de auditoría...</span>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredData?.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {auditData.length === 0 ? 'No hay consultas registradas' : 'No se encontraron registros'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {auditData.length === 0
                    ? 'Realiza consultas VIN en la sección de Consulta para ver el historial aquí.'
                    : 'Intenta ajustar los filtros o el rango de fechas para ver más resultados.'}
                </p>
                {auditData.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setFilters({})}
                    iconName="RotateCcw"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Limpiar todos los filtros
                  </Button>
                )}
              </div>
            )}

            {/* Audit Table */}
            {!isLoading && filteredData?.length > 0 && (
              <>
                <AuditTable
                  data={paginatedData}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onRowExpand={handleRowExpand}
                  expandedRows={expandedRows}
                />

                {/* Pagination */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredData?.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              </>
            )}
          </div>
        </div>
      </main>
      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        totalRecords={filteredData?.length}
      />
      <QuickActionButton />
    </div>
  );
};

export default HistoryAndAuditTrail;